import { useState } from "react";
import { Link } from "react-router-dom";
import { sourceShortLabel } from "../leads/leadFilterLabels";
import { Card } from "../../components/ui/Card";
import { StatusPill } from "../../components/ui/StatusPill";
import { ContactActions } from "../../components/ui/ContactActions";
import { LastActivityCell } from "../activities/LastActivityCell";
import { useMembersIndex } from "../team/useMembersIndex";
import { formatRelative } from "../../lib/format";
import type { LatestLeadRow } from "./api";

type TabKey = "direct" | "meta" | "attention";

interface Props {
  direct: LatestLeadRow[];
  meta: LatestLeadRow[];
  attention: LatestLeadRow[];
}

const TABS: { key: TabKey; label: string; viewAllHref?: string }[] = [
  { key: "direct", label: "Direct", viewAllHref: "/leads?source=website_form" },
  { key: "meta", label: "Meta", viewAllHref: "/leads?source=meta_ads" },
  { key: "attention", label: "Needs attention" },
];

export function LatestLeadsCard({ direct, meta, attention }: Props) {
  const [active, setActive] = useState<TabKey>("direct");
  const tabMeta = TABS.find((t) => t.key === active)!;
  const rows = active === "direct" ? direct : active === "meta" ? meta : attention;

  const counts: Record<TabKey, number> = {
    direct: direct.length,
    meta: meta.length,
    attention: attention.length,
  };

  return (
    <Card
      title="Latest leads"
      subtitle={
        active === "attention"
          ? "In 'new' or 'follow_up' with no activity in the last 24h"
          : "Most recent 5 — view all to see the full list"
      }
      right={
        tabMeta.viewAllHref ? (
          <Link to={tabMeta.viewAllHref} className="btn btn-secondary btn-sm">
            View all →
          </Link>
        ) : null
      }
    >
      <div className="tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={active === tab.key}
            className={`tab${active === tab.key ? " active" : ""}`}
            onClick={() => setActive(tab.key)}
          >
            {tab.label}
            <span className="muted" style={{ marginLeft: 6 }}>
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      <div className="tab-panel" role="tabpanel">
        {active === "direct" && <DirectTable rows={rows} />}
        {active === "meta" && <MetaTable rows={rows} />}
        {active === "attention" && <AttentionTable rows={rows} />}
      </div>
    </Card>
  );
}

function EmptyRow({ message }: { message: string }) {
  return <p className="muted">{message}</p>;
}

function AssignedCell({ id }: { id: string | null }) {
  const { resolveName } = useMembersIndex();
  if (!id) return <span className="muted">Unassigned</span>;
  return <>{resolveName(id)}</>;
}

function LeadNameCell({ row, sub }: { row: LatestLeadRow; sub?: string | null }) {
  return (
    <td>
      <Link to={`/leads/${row.id}`} className="row-link">
        {row.name ?? "(no name)"}
      </Link>
      {sub ? <div className="row-sub">{sub}</div> : null}
    </td>
  );
}

function ContactCell({ row }: { row: LatestLeadRow }) {
  return (
    <td>
      <div className="contact-cell">
        <div>
          <div>{row.phoneE164 ?? "—"}</div>
          {row.email ? <div className="row-sub">{row.email}</div> : null}
        </div>
        <ContactActions
          phone={row.phoneE164}
          email={row.email}
          variant="menu"
          name={row.name}
        />
      </div>
    </td>
  );
}

function DirectTable({ rows }: { rows: LatestLeadRow[] }) {
  if (rows.length === 0)
    return <EmptyRow message="No form submissions yet." />;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Lead</th>
            <th>Contact</th>
            <th>Form / campaign</th>
            <th>Status</th>
            <th>Assigned</th>
            <th>Received</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <LeadNameCell row={row} sub={row.company} />
              <ContactCell row={row} />
              <td>
                <div>{row.sourceCampaignName ?? row.sourceFormId ?? "—"}</div>
                {row.sourceCampaignName && row.sourceFormId ? (
                  <div className="row-sub">{row.sourceFormId}</div>
                ) : null}
              </td>
              <td>
                <StatusPill status={row.status} />
                <LastActivityCell
                  leadId={row.id}
                  type={row.lastLoggedActivityType}
                  at={row.lastLoggedActivityAt}
                />
              </td>
              <td><AssignedCell id={row.assignedTo} /></td>
              <td title={row.createdAt}>{formatRelative(row.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MetaTable({ rows }: { rows: LatestLeadRow[] }) {
  if (rows.length === 0) return <EmptyRow message="No Meta leads yet." />;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Lead</th>
            <th>Contact</th>
            <th>Campaign / ad</th>
            <th>Status</th>
            <th>Assigned</th>
            <th>Received</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <LeadNameCell row={row} sub={row.company} />
              <ContactCell row={row} />
              <td>
                <div>{row.sourceCampaignName ?? "—"}</div>
                {row.sourceAdName ? <div className="row-sub">{row.sourceAdName}</div> : null}
              </td>
              <td>
                <StatusPill status={row.status} />
                <LastActivityCell
                  leadId={row.id}
                  type={row.lastLoggedActivityType}
                  at={row.lastLoggedActivityAt}
                />
              </td>
              <td><AssignedCell id={row.assignedTo} /></td>
              <td title={row.createdAt}>{formatRelative(row.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AttentionTable({ rows }: { rows: LatestLeadRow[] }) {
  if (rows.length === 0)
    return <EmptyRow message="All clear — nothing needs attention right now." />;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Lead</th>
            <th>Source</th>
            <th>Status</th>
            <th>Last activity</th>
            <th>Assigned</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <LeadNameCell row={row} sub={row.company} />
              <td>
                <span className="badge">
                  {sourceShortLabel(row.source)}
                </span>
                {row.sourceCampaignName ? (
                  <div className="row-sub">{row.sourceCampaignName}</div>
                ) : null}
              </td>
              <td>
                <StatusPill status={row.status} />
                <LastActivityCell
                  leadId={row.id}
                  type={row.lastLoggedActivityType}
                  at={row.lastLoggedActivityAt}
                />
              </td>
              <td title={row.lastActivityAt ?? ""}>
                {row.lastActivityAt ? formatRelative(row.lastActivityAt) : "—"}
              </td>
              <td><AssignedCell id={row.assignedTo} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
